import { createTheme, ThemeOptions } from '@mui/material/styles';

/**
 * BaseQueue design tokens — mirror basequeue-web-new/theme.ts and the
 * admin-prototype's assets/css/app.css (same brand blue, dark sidebar,
 * white cards, status chip palette).
 */
export const colors = {
  primary: '#076FF7',
  primaryDark: '#065CC6',
  primaryLight: '#EFF6FF',

  textPrimary: '#1F2937',
  textSecondary: '#6B7280',
  textTertiary: '#9CA3AF',
  textWhite: '#FFFFFF',

  backgroundDefault: '#F4F6FB',
  backgroundPaper: '#FFFFFF',

  border: '#E5E7EB',
  inputBorder: '#D1D5DB',

  error: '#DC2626',
  success: '#16A34A',
  warning: '#F59E0B',

  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
};

export const brand = {
  sidebarBg: '#0B1220',
  sidebarFg: '#CBD5E1',
  sidebarActive: '#1D2A44',
  gradBrand: 'linear-gradient(135deg, #076FF7 0%, #065CC6 100%)',
  gradAdmin: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
  shadow: '0 1px 2px rgba(16, 24, 40, .05)',
};

/** Avatar gradients (cycled by name length — matches prototype BQ.avatar). */
export const avatarGradients = [
  'linear-gradient(135deg,#3B82F6,#1D4ED8)',
  'linear-gradient(135deg,#A78BFA,#7C3AED)',
  'linear-gradient(135deg,#FBBF24,#EA580C)',
  'linear-gradient(135deg,#34D399,#059669)',
];

/** Doughnut/bar palette used across charts (matches prototype catColors). */
export const chartColors = ['#076FF7', '#7C3AED', '#F59E0B', '#10B981', '#EF4444', '#0EA5E9', '#EC4899', '#84CC16'];

/**
 * Status chip palette — keys map to the `status` strings in the mock data.
 * Each entry is [background, text]. Mirrors the .chip-* rules in app.css.
 */
export const chipColors: Record<string, [string, string]> = {
  completed: ['rgba(16,185,129,.12)', '#059669'],
  active: ['rgba(22,163,74,.12)', '#15803D'],
  paid: ['rgba(22,163,74,.12)', '#15803D'],
  success: ['rgba(22,163,74,.12)', '#15803D'],
  enabled: ['rgba(22,163,74,.12)', '#15803D'],
  booked: ['rgba(59,130,246,.12)', '#2563EB'],
  info: ['rgba(59,130,246,.12)', '#2563EB'],
  upcoming: ['rgba(59,130,246,.12)', '#2563EB'],
  waiting: ['rgba(96,165,250,.14)', '#2563EB'],
  in_progress: ['rgba(245,158,11,.14)', '#B45309'],
  serving: ['rgba(245,158,11,.14)', '#B45309'],
  pending: ['rgba(245,158,11,.14)', '#B45309'],
  warning: ['rgba(245,158,11,.14)', '#B45309'],
  trial: ['rgba(245,158,11,.14)', '#B45309'],
  hold: ['rgba(249,115,22,.14)', '#C2410C'],
  skipped: ['rgba(251,191,36,.16)', '#B45309'],
  cancelled: ['rgba(107,114,128,.14)', '#4B5563'],
  inactive: ['rgba(107,114,128,.14)', '#4B5563'],
  paused: ['rgba(107,114,128,.14)', '#4B5563'],
  disabled: ['rgba(107,114,128,.14)', '#4B5563'],
  no_show: ['rgba(239,68,68,.12)', '#DC2626'],
  failed: ['rgba(239,68,68,.12)', '#DC2626'],
  overdue: ['rgba(239,68,68,.12)', '#DC2626'],
  error: ['rgba(239,68,68,.12)', '#DC2626'],
  expired: ['rgba(239,68,68,.12)', '#DC2626'],
};

const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: { main: colors.primary, light: colors.primaryLight, dark: colors.primaryDark, contrastText: colors.textWhite },
    secondary: { main: '#7C3AED', light: '#A78BFA', dark: '#5B21B6', contrastText: colors.textWhite },
    error: { main: colors.error, light: '#FEE2E2', dark: '#B91C1C' },
    warning: { main: colors.warning, light: '#FEF3C7', dark: '#D97706' },
    info: { main: colors.primary, light: colors.primaryLight, dark: colors.primaryDark },
    success: { main: colors.success, light: '#DCFCE7', dark: '#15803D' },
    background: { default: colors.backgroundDefault, paper: colors.backgroundPaper },
    text: { primary: colors.textPrimary, secondary: colors.textSecondary, disabled: colors.textTertiary },
    divider: colors.border,
  },
  typography: {
    fontFamily: ['"Inter"', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'sans-serif'].join(','),
    h1: { fontSize: '2.25rem', fontWeight: 800, lineHeight: 1.1 },
    h2: { fontSize: '1.5rem', fontWeight: 700, lineHeight: '32px' },
    h3: { fontSize: '1.25rem', fontWeight: 700, lineHeight: '28px' },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: '24px' },
    h5: { fontSize: '1rem', fontWeight: 600, lineHeight: '24px' },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: '20px' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: { borderRadius: 14 },
  spacing: 8,
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, textTransform: 'none', fontWeight: 600, boxShadow: 'none' },
      },
      defaultProps: { disableElevation: true },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 14, border: `1px solid ${colors.border}`, boxShadow: brand.shadow },
      },
    },
    MuiPaper: { defaultProps: { elevation: 0 } },
    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: colors.backgroundPaper,
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary, borderWidth: 2 },
        },
        notchedOutline: { borderColor: colors.inputBorder },
      },
    },
  },
};

const theme = createTheme(themeOptions);

export default theme;
